/**
 * karmabooking-api
 *
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2020 Karmabunny
 */

import { ApiRequest, stringify } from './base';

/**
 *
 */
export interface ApiError extends Error {
    name: "ApiError";
    res?: Response;
    status: number;
}

export async function newApiError(req: ApiRequest, res: Response): Promise<ApiError> {
    const { method, endpoint } = req;
    const params = stringify(req.query);

    try {
        const body = await res.json();

        if (body.status && body.validations) {
            const message = Object.values(body.validations as Record<string, string | string[]>)
                .map(message => (
                    typeof message === "string"
                    ? message
                    : message.join(', ')
                ))
                .join(', ');

            const error = new Error(message) as ApiError;
            error.name = "ApiError";
            error.status = res.status;
            return error;
        }
        else if (body.status && body.message) {
            const error = new Error(body.message) as ApiError;
            error.name = "ApiError";
            error.status = res.status;
            return error;
        }
        else if (typeof body.error === "string") {
            const error = new Error(body.error) as ApiError;
            error.name = "ApiError";
            error.status = res.status;
            return error;
        }
    }
    catch (e) {}

    const message = `${method} ${endpoint}${params} ${res.status} (${res.statusText})`;
    const error = new Error(message) as ApiError;
    error.name = "ApiError";
    error.status = res.status;
    error.res = res;
    return error;
}

export function isApiError(test: unknown): test is ApiError {
    return !!test && test instanceof Error && test.name === "ApiError";
}

export function assertApiError(test: unknown): asserts test is ApiError {
    if (!isApiError(test)) throw test;
}

/**
 *
 */
export interface TimeoutError extends Error {
    name: "TimeoutError";
    timeout: number;
    url: string;
}

export function newTimeoutError(url: string, req: ApiRequest, timeout: number): TimeoutError {
    const { method, endpoint } = req;
    const params = stringify(req.query);
    const seconds = (timeout / 1000).toFixed(0);

    const error = new Error(`API timeout: ${method} ${endpoint}${params} (${seconds}s)`) as TimeoutError;
    error.name = "TimeoutError";
    error.timeout = timeout;
    error.url = url + endpoint + params;
    return error;
}

export function isTimeoutError(test: unknown): test is TimeoutError {
    return !!test && test instanceof Error && test.name === "TimeoutError";
}

export function assertTimeoutError(test: unknown): asserts test is TimeoutError {
    if (!isTimeoutError(test)) throw test;
}